import { ApiType, settings } from "config";
import {
  AddressesDTO,
  CredicCardsDTO,
  OrdersDTO,
  ProductsDTO,
  ProductsFilterDTO,
  ProductSKUsDTO,
  ProductsTableEntityDTO,
  ShippingGeoCountriesDTO,
  StoreDTO,
  UserDTO,
} from "models";
import { GeneralStore } from "mobx-stores";
import {
  AuthPayload,
  FetchOptions,
  LoginPayload,
  PageQueryParams,
  RegisterPayload,
} from "./api.types";
import { Fetch } from "./fetch";
import {
  MediaContentDTO,
  Page,
  AttributeGroupDTO,
  ProductSkuDTO,
} from "@touche-lts/panel-ui/dist";
import { ShippingDTO } from "models/shipping";
import { ProductsTableSortProps } from "mobx-stores/utils";
import { SKUFilterDTO } from "models/skus-filter";
import { OrdersTableSearchProps } from "mobx-stores/utils/orders-table-base/orders-table-base.types";
import { ReviewDTO } from "models/review";
import { StoreAttributes } from "models/stores";
import {
  StatisticsHighlights,
  StatisticsPayloadDTO,
  StatisticsTableEntryDTO,
} from "models/statistics";
import {
  ActionHistoryTableSearchProps,
  ActionHistoryTableSortProps,
} from "mobx-stores/utils/action-history-table-base/action-history-table-base.types";
import { Notifications } from "models/notifications";
import {
  DepartmentCategory,
  Department,
  DepartmentTypes,
  DepartmentSubcategory,
  DepartmentFilterPayload,
  DepartmentCategoryFilters,
} from "models/departments";
import {
  LegalTableEntityDTO,
} from "models/legal-table-entry";
import { GlobalSearchDTO } from "models/globalSearch";
import { formatErrorMessage } from "utilities";

  const getHeaders = () => {
  const jwt =JSON.parse(window.localStorage.getItem('jwt')) 
  const headerAuth = `${jwt.tokenType} ${jwt.accessToken}`
  return {
    'Content-Type': 'application/json',
    Authorization: headerAuth,
  }
}

export class Api {
  private get: Fetch["get"];
  private post: Fetch["post"];
  private put: Fetch["put"];
  private patch: Fetch["patch"];
  private delete: Fetch["delete"];
  private upload: Fetch["upload"];
  private uploadSigned: Fetch["uploadSigned"];

  private s3config = {
    bucketName: null,
    dirName: "images" /* optional */,
    region: null,
    accessKeyId: null,
    secretAccessKey: null,
  };

  constructor(generalStore: GeneralStore) {
    const fetchService = new Fetch({
      onError: generalStore.handleApiError,
      onFetchStart: generalStore.setLoadingOn,
      onFetchEnd: generalStore.setLoadingOff,
    });
    this.get = fetchService.get;
    this.post = fetchService.post;
    this.put = fetchService.put;
    this.patch = fetchService.patch;
    this.delete = fetchService.delete;
    this.upload = fetchService.upload;
    this.uploadSigned = fetchService.uploadSigned;
  }

  private generateUrl = (
    url: string,
    options?: { apiType?: ApiType; pageQueryParams?: PageQueryParams }
  ): string => {
    const apiUrl = settings[options?.apiType || ApiType.MAIN];
    const pageQueryParamsUrl = this.generatePageQueryParamsUrl(
      options?.pageQueryParams
    );
    return `${apiUrl}${url}${pageQueryParamsUrl}`;
  };

  private generatePageQueryParamsUrl = (params: PageQueryParams): string => {
    if (!params) return "";
    return `?page=${encodeURIComponent(params?.page)}&size=${encodeURIComponent(params.size)}&sort=${encodeURIComponent(params?.sortProp)},${encodeURIComponent(params?.sortDirection)}`;
  };

  /** GCP file upload */
   static async uploadFile(
    file: any,
    progressCallback?: (progressEvent: ProgressEvent) => void
  ): Promise<{ location: string }> {

  const jwt = JSON.parse(window.localStorage.getItem('jwt')) 
  const headerAuth = `${jwt.tokenType} ${jwt.accessToken}`

     try {
      let res:any = await fetch(`${settings[ApiType.MAIN]}/mediaContent/presigned?objectKey=${encodeURIComponent(file?.path)}`,{
        method : "GET",
        headers : getHeaders()
      });

      res = await res?.json();
      const location = res?.location;

      if(res?.url && res?.location){
       try {
        const formData = new FormData();

        if(file && file?.path) {
          formData.append("body",file ,file?.path)
        }

        res = await fetch(res?.url,{
          method : "POST",
         headers : {
          Authorization: headerAuth,
          },
          body: formData
        });

        res = await res.json();

        return 
       } catch (error) {

       }
      }

      return {location };
      
     } catch (error) {
      
     }

  }

  public uploadFileSigned = async (
    file: File
  ): Promise<{ location: string }> => {
    const url = this.generateUrl(
      `/mediaContent/presigned?objectKey=${encodeURIComponent(file.name)}`,
      {
        apiType: ApiType.MAIN,
      }
    );
    const res = await this.get(url);
    if (!res) return;
    const result = await this.uploadSigned(file, res);
    return result;
  };

  public userApi = {
    login: async (payload: LoginPayload): Promise<AuthPayload> => {
      const url = this.generateUrl("/auth/login");
      return await this.post(url, payload);
    },
    register: async (payload: RegisterPayload): Promise<AuthPayload> => {
      const url = this.generateUrl("/auth/register");
      return await this.post(url, payload);
    },
    socialLogin: async (payload: { provider: string; token: string }): Promise<AuthPayload> => {
      const url = this.generateUrl("/social-login", { apiType: ApiType.USER });
      return await this.post(url, { ...payload, client: 'marketplace' });
    },
    getUser: async (userId: number | string): Promise<UserDTO> => {
      const url = this.generateUrl(`/users/${userId}`, {
        apiType: ApiType.USER,
      });
      return await this.get(url);
    },
    updateUser: async (
      userId: number | string,
      payload: Partial<UserDTO>
    ): Promise<UserDTO> => {
      const url = this.generateUrl(`/user/${userId}`, {
        apiType: ApiType.USER,
      });
      return await this.put(url, payload);
    },
    updateUserPassword: async (payload: Partial<UserDTO>): Promise<UserDTO> => {
      const url = this.generateUrl("/user/changePassword", {
        apiType: ApiType.USER,
      });
      return await this.post(url, payload);
    },
    getCreditCards: async (
      userId: number | string
    ): Promise<CredicCardsDTO[]> => {
      const url = this.generateUrl(`/user/${userId}/creditcards`, {
        apiType: ApiType.USER,
      });
      return await this.get(url);
    },
    addCreditCard: async (userId: number, creditCard: CredicCardsDTO) => {
      const url = this.generateUrl(`/user/${userId}/creditcards`, {
        apiType: ApiType.USER,
      });
      await this.post(url, creditCard);
    },
    deleteCreditCard: async (
      userId: number | string,
      creditCardId: number | string
    ): Promise<CredicCardsDTO> => {
      let url = this.generateUrl(
        `/user/${userId}/creditcards/${creditCardId}`,
        { apiType: ApiType.USER }
      );
      return await this.delete(url);
    },
    getUserAddresses: async (
      userId: number | string
    ): Promise<AddressesDTO[]> => {
      const url = this.generateUrl(`/user/${userId}/address`, {
        apiType: ApiType.USER,
      });
      return await this.get(url);
    },
    addUserAddress: async (
      userId: number | string,
      payload: Partial<AddressesDTO>
    ): Promise<UserDTO> => {
      const url = this.generateUrl(`/user/${userId}/address`, {
        apiType: ApiType.USER,
      });
      return await this.post(url, payload);
    },
    updateUserAddress: async (
      userId: number | string,
      addressId: number | string,
      payload: Partial<AddressesDTO>
    ): Promise<UserDTO> => {
      const url = this.generateUrl(`/user/${userId}/address/${addressId}`, {
        apiType: ApiType.USER,
      });
      return await this.put(url, payload);
    },
    deleteUserAddress: async (
      userId: number | string,
      addressId: number | string
    ): Promise<Partial<UserDTO>> => {
      let url = this.generateUrl(`/user/${userId}/address/${addressId}`, {
        apiType: ApiType.USER,
      });
      return await this.delete(url);
    },
    getAddressShippingCountries: async (): Promise<
      ShippingGeoCountriesDTO[]
    > => {
      const url = this.generateUrl("/geo/countries", {
        apiType: ApiType.MAIN,
      });
      return await this.get(url);
    },
    getCartByUserId: async (userId: number | string): Promise<any> => {
      // TODO-A make model here
      const url = this.generateUrl(`/cart/users/${userId}/products`, {
        apiType: ApiType.MAIN,
      });
      return await this.get(url);
    },
    addCartByUserId: async (
      userId: number | string,
      payload: any
    ): Promise<AuthPayload> => {
      const url = this.generateUrl(`/cart/users/${userId}/products`, {
        apiType: ApiType.MAIN,
      });
      return await this.post(url, payload);
    },
    updateCartByProductId: async (
      userId: number | string,
      productId: number | string,
      payload: any
    ): Promise<any> => {
      const url = this.generateUrl(
        `/cart/users/${userId}/products/${productId}`
      );
      return await this.put(url, payload);
    },
    deleteCartByProductId: async (
      userId: number | string,
      productId: number | string
    ): Promise<any> => {
      const url = this.generateUrl(
        `/cart/users/${userId}/products/${productId}`
      );
      return await this.delete(url);
    },
    setUserCartChecked: async (
      userId: number | string,
      payload: any
    ): Promise<any> => {
      const url = this.generateUrl(`/cart/users/${userId}/products/checked`, {
        apiType: ApiType.MAIN,
      });
      return await this.post(url, payload);
    },
    getUserTemp: async (): Promise<AuthPayload> => {
      const url = this.generateUrl(`/user/temp`, {
        apiType: ApiType.USER,
      });
      return await this.get(url);
    },
  };

  public storeApi = {
    getStores: async (options?: any, searchStore?: string): Promise<any> => {
      // TODO-A To put correct model type
      let url = this.generateUrl("/stores/paged", {
        pageQueryParams: options?.pageQueryParams,
      });
      if (!!searchStore) {
        url += "&storeName=" + encodeURIComponent(searchStore);
      }
      return await this.get(url);
    },
    getActiveStores: async (
      options?: any,
      searchStore?: string
    ): Promise<any> => {
      // TODO-A To put correct model type
      let url = this.generateUrl("/stores/active/paged", {
        pageQueryParams: options?.pageQueryParams,
      });
      if (!!searchStore) {
        url += "&storeName=" + encodeURIComponent(searchStore);
      }
      return await this.get(url);
    },
    createStore: async (storeDto: StoreDTO): Promise<StoreDTO> => {
      const url = this.generateUrl("/stores");
      return await this.post(url, storeDto);
    },
    getStoreById: async (storeId: number | string): Promise<StoreDTO> => {
      const url = this.generateUrl(`/stores/${storeId}`);
      return await this.get(url);
    },
    getStoreByUser: async (
      emailAddress: string,
      options?: FetchOptions
    ): Promise<StoreDTO[]> => {
      const url = this.generateUrl(`/stores/users/${encodeURIComponent(emailAddress)}`);
      return await this.get(url, options);
    },
    updateStore: async (storeDto: StoreDTO): Promise<StoreDTO> => {
      const url = this.generateUrl(`/stores/${storeDto.id}`);
      return await this.put(url, storeDto);
    },
    suspendStore: async (storeId: number | string): Promise<StoreDTO> => {
      const url = this.generateUrl(`/stores/${storeId}`);
      return await this.delete(url);
    },
    getProductById: async (
      productId: number | string,
      options?: FetchOptions
    ): Promise<ProductsDTO> => {
      const url = this.generateUrl(`/products/${productId}`);
      return await this.get(url, options);
    },
    getAllProductsFiltered: async (
      storeId: number | string,
      filter: Partial<ProductsFilterDTO>,
      options?: FetchOptions<ProductsTableSortProps>
    ): Promise<Page<ProductsTableEntityDTO>> => {
      const url = this.generateUrl(`/stores/${storeId}/products/tableEntries`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filter, options);
    },
    getAttributeGroups: async (
      storeId: number | string,
      options?: FetchOptions
    ): Promise<AttributeGroupDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/attributeGroups`);
      return await this.get(url, options);
    },
    updateAttributeGroups: async (
      storeId: number | string,
      attributeGroups: AttributeGroupDTO[],
      options?: FetchOptions
    ): Promise<AttributeGroupDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/attributeGroups`);
      return await this.put(url, attributeGroups, options);
    },
    addShipping: async (
      storeId: number | string,
      shipping: ShippingDTO,
      options?: FetchOptions
    ): Promise<ShippingDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/shippings`);
      return await this.post(url, shipping, options);
    },
    removeShipping: async (
      storeId: number | string,
      shippingId: number | string,
      options?: FetchOptions
    ): Promise<ShippingDTO[]> => {
      const url = this.generateUrl(
        `/stores/${storeId}/shippings/${shippingId}`
      );
      return await this.delete(url, options);
    },
    addNewProduct: async (
      storeId: number | string,
      products: Partial<ProductsDTO>,
      options?: FetchOptions
    ): Promise<ProductsDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/products`);
      try {
        await this.post(url, products, options);
        return;
      } catch (error) {
        throw new Error(formatErrorMessage(error))
      }
    },
    updateImage: async (
      storeId: number | string,
      images: MediaContentDTO[],
      options?: FetchOptions
    ): Promise<MediaContentDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/images`);
      return await this.put(url, images, options);
    },
    storeOrdersFiltered: async (
      storeId: number | string,
      filters: Partial<OrdersTableSearchProps>,
      options?: FetchOptions
    ): Promise<Page<OrdersDTO>> => {
      const url = this.generateUrl(`/stores/${storeId}/orders`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filters, options);
    },
    getOrderById: async (
      orderId: number | string,
      options?: FetchOptions
    ): Promise<AttributeGroupDTO[]> => {
      const url = this.generateUrl(`/orders/${orderId}`);
      return await this.get(url, options);
    },
    getSingleStore: async (
      storeId: number | string,
      filter: Partial<ProductsFilterDTO>,
      options?: any
    ): Promise<Page<ProductsTableEntityDTO>> => {
      const url = this.generateUrl(`/stores/${storeId}/products/public`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filter, options);
    },
    getSingleStoreAttributes: async (
      storeId: number | string,
      options?: FetchOptions
    ): Promise<StoreAttributes[]> => {
      const url = this.generateUrl(
        `/stores/${storeId}/products/public/attributes`
      );
      return await this.get(url, options);
    },
    customerOrders: async (
      userId: number | string,
      filters: Partial<OrdersTableSearchProps>,
      options?: FetchOptions
    ): Promise<Page<OrdersDTO>> => {
      const url = this.generateUrl(`/customers/${userId}/orders`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filters, options);
    },
    ordersCheckout: async (payload: any): Promise<any> => {
      const url = this.generateUrl(`/orders/checkout/MARKETPLACE`);
      return await this.post(url, payload);
    },
    sendOrderToShopify: async (payload: any): Promise<any> => {
      const url = this.generateUrl(`/orders/checkoutToShopify`);
      return await this.post(url, payload);
    },
  };

  public shippingApi = {
    updateShipping: async (
      shipping: ShippingDTO,
      options?: FetchOptions
    ): Promise<ShippingDTO> => {
      const url = this.generateUrl(`/shippings/${shipping.id}`);
      return await this.put(url, shipping, options);
    },
    getShipping: async (
      storeId: number | string,
      options?: FetchOptions
    ): Promise<ShippingDTO[]> => {
      const url = this.generateUrl(`/stores/${storeId}/shippings`);
      return await this.get(url, options);
    },
  };

  public productsApi = {
    updateProducts: async (
      products: Partial<ProductsDTO>,
      productId: number | string,
      options?: FetchOptions
    ): Promise<ProductsDTO> => {
      const url = this.generateUrl(`/products/${productId}`);
      return await this.put(url, products, options);
    },
    getProductAttributes: async (
      productId: number | string,
      options?: FetchOptions
    ): Promise<AttributeGroupDTO[]> => {
      const url = this.generateUrl(`/products/${productId}/attributeGroups`);
      return await this.get(url, options);
    },
    getProductImages: async (
      productId: number | string,
      options?: FetchOptions
    ): Promise<MediaContentDTO[]> => {
      const url = this.generateUrl(`/products/${productId}/images`);
      return await this.get(url, options);
    },
    getProductSkus: async (
      productId: number | string,
      options?: FetchOptions
    ): Promise<Page<ProductSkuDTO>> => {
      const url = this.generateUrl(`/products/${productId}/skus`);
      return await this.get(url, options);
    },
    updateProductImages: async (
      productId: number | string,
      productImages: MediaContentDTO[],
      options?: FetchOptions
    ): Promise<MediaContentDTO[]> => {
      const url = this.generateUrl(`/products/${productId}/images`);
      return await this.put(url, productImages, options);
    },
    updateAttributeGroups: async (
      productId: number | string,
      attributeGroups: AttributeGroupDTO[],
      options?: FetchOptions
    ): Promise<AttributeGroupDTO[]> => {
      const url = this.generateUrl(`/products/${productId}/attributeGroups`);
      return await this.put(url, attributeGroups, options);
    },
    updateAllProductSkus: async (
      productId: number | string,
      tableSkus: ProductSKUsDTO[],
      options?: FetchOptions
    ): Promise<any> => {
      const url = this.generateUrl(`/products/${productId}/skus`);
      return await this.patch(url, tableSkus, options);
    },
    updateAllProductGallerySkus: async (
      productId: number | string,
      skus: ProductSKUsDTO[],
      options?: FetchOptions
    ): Promise<any> => {
      const url = this.generateUrl(`/products/${productId}/skusUpdate`);
      return await this.post(url, skus, options);
    },
    allFilteredSorted: async (
      productId: number | string,
      filter: SKUFilterDTO,
      options?: FetchOptions<ProductsTableSortProps>
    ): Promise<Page<ProductSKUsDTO>> => {
      const url = this.generateUrl(`/products/${productId}/skus`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filter, options);
    },
  };

  public productSkuApi = {
    addImage: async (args: {
      productSkuId: number | string;
      image: MediaContentDTO;
      options?: FetchOptions;
    }): Promise<MediaContentDTO[]> => {
      const { image, productSkuId, options } = args;
      const url = this.generateUrl(`/productSKUs/${productSkuId}/images`);
      return await this.post(url, image, options);
    },
    removeImage: async (args: {
      productSkuId: number | string;
      imageId: number | string;
      options?: FetchOptions;
    }): Promise<MediaContentDTO[]> => {
      const { imageId, productSkuId, options } = args;
      const url = this.generateUrl(
        `/productSKUs/${productSkuId}/images/${imageId}`
      );
      return await this.delete(url, options);
    },
  };

  public reviewsApi = {
    setStoreProductReviews: async (
      payload: Partial<ReviewDTO>
    ): Promise<ReviewDTO> => {
      const url = this.generateUrl(`/reviews`);
      return await this.post(url, payload);
    },
  };

  public statisticsApi = {
    getHighLights: async (
      filter?: Partial<StatisticsPayloadDTO>
    ): Promise<StatisticsHighlights> => {
      const url = this.generateUrl(`/statistics/highlights`);
      return await this.post(url, filter);
    },
    getStoreStatisticsGraph: async (
      filter: Partial<ProductsFilterDTO>,
      options?: FetchOptions<ProductsTableSortProps>
    ): Promise<StatisticsTableEntryDTO[]> => {
      const url = this.generateUrl(`/statistics/stores/historic`);
      return await this.post(url, filter);
    },
    getAllStoreStatistics: async (
      filter: Partial<ProductsFilterDTO>,
      options?: FetchOptions<ProductsTableSortProps>
    ): Promise<StatisticsTableEntryDTO[]> => {
      const url = this.generateUrl(`/statistics/stores`);
      return await this.post(url, filter);
    },
    getStoreStatistics: async (
      storeId: number | string,
      filter: Partial<ProductsFilterDTO>,
      options?: FetchOptions<ProductsTableSortProps>
    ): Promise<StatisticsTableEntryDTO[]> => {
      const url = this.generateUrl(`/statistics/stores/${storeId}/products`);
      return await this.post(url, filter);
    },
    getTopStoreProducts: async (options?: FetchOptions): Promise<any> => {
      const url = this.generateUrl(`/statistics/top-stores-products`);
      return await this.get(url, options);
    },
    getStoreAdminHighlights: async (
      storeId: number | string,
      filter?: Partial<StatisticsPayloadDTO>
    ): Promise<any> => {
      const url = this.generateUrl(`/statistics/store-admin/${storeId}/highlights`);
      return await this.post(url, filter ?? {});
    },
  };

  public notificationsApi = {
    getNotifications: async (): Promise<Notifications[]> => {
      const url = this.generateUrl(`/notifications`);
      return await this.get(url);
    },
    getNotificationsByUser: async (
      userId: number
    ): Promise<Notifications[]> => {
      const url = this.generateUrl(`/notifications/${userId}`);
      return await this.get(url,{silentProcess: true}); // silenetProcess is disable the loader 
    },
    deleteNotification: async (notificationId: number) => {
      const url = this.generateUrl(`/notifications/${notificationId}`);
      return await this.delete(url);
    },
  };

  public actionHistoryApi = {
    getActionHistory: async (
      userId: number | string,
      filter: Partial<ActionHistoryTableSearchProps>,
      options?: FetchOptions<ActionHistoryTableSortProps>
    ): Promise<Page<ActionHistoryTableSearchProps>> => {
      const url = this.generateUrl(`/action-history/users/${userId}`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filter, options);
    },
  };

  public departmentsApi = {
    getAllDepartments: async (): Promise<Department[]> => {
      const url = this.generateUrl(`/ui-labels/departments`);
      return await this.get(url);
    },
    getDepartmentCategories: async (
      departmentId: number
    ): Promise<DepartmentCategory[]> => {
      const url = this.generateUrl(
        `/ui-labels/departments/${departmentId}/categories`
      );
      return await this.get(url);
    },
    getDepartmentSubcategories: async (
      categoryId: number
    ): Promise<DepartmentSubcategory[]> => {
      const url = this.generateUrl(
        `/ui-labels/categories/${categoryId}/subcategories`
      );
      return await this.get(url);
    },
    getDepartmentTypes: async (
      subCategoryId: number
    ): Promise<DepartmentTypes[]> => {
      const url = this.generateUrl(
        `/ui-labels/subcategories/${subCategoryId}/types`
      );
      return await this.get(url);
    },
    getDepartmentSubtypes: async (
      typeId: number
    ): Promise<DepartmentTypes[]> => {
      const url = this.generateUrl(`/ui-labels/${typeId}/subtypes`);
      return await this.get(url);
    },
    getAllProductsByDepartmentCategory: async (
      filter: Partial<DepartmentFilterPayload>,
      options?: any
    ): Promise<Page<Partial<ProductsDTO>>> => {
      const url = this.generateUrl(`/products/all`, {
        pageQueryParams: options?.pageQueryParams,
      });
      return await this.post(url, filter, options);
    },
    getFiltersByCategory: async (
      categoryId: number
    ): Promise<DepartmentCategoryFilters> => {
      const url = this.generateUrl(`/ui-labels/category/${categoryId}/filters`);
      return await this.get(url);
    },
  };

  public legalApi = {
    getTerms: async (): Promise<Page<LegalTableEntityDTO>> => {
      const url = this.generateUrl(`/legal-pages`);
      return await this.get(url);
    },
  };

  public searchApi = {
    globalSearch: async (
      name: string,
      filter?: any,
      options?: any
    ): Promise<GlobalSearchDTO> => {
      const url = this.generateUrl(`/globalSearch?name=${encodeURIComponent(name)}`);
      return await this.post(url, filter, options);
    },
  };
}
